import React, { useCallback, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { cachedClientLocationCountry, getClientLocationCountry } from '@yme/api';
import { CloseIcon } from '@yme/assets/icons';
import { Button, NO_ICON, SIZE_36, SIZE_40, ICON_ONLY, TEXT_VARIANT, GHOST_VARIANT, } from '@yme/atoms/button';
import { Dropdown } from '@yme/atoms/dropdown';
import { GridWrapper } from '@yme/atoms/grid';
import { SpinnerLoader } from '@yme/atoms/spinner-loader';
import { OPTANON_ALERT_BOX_CLOSED } from '@yme/common/constants';
import { TARGET_PAGE_SERVICE_ENDPOINT } from '@yme/common/endpoints';
import { setLocationMismatchInitialCountry, setLocationMismatchInitialLanguage, } from '@yme/common/redux/common/action';
import { changeLanguageList } from '@yme/common/redux/common/reducer';
import { useWindowSize } from '@yme/hooks/useWindowSize';
import { COOKIE_EXPIRE_DAYS, PREFERRED_COUNTRY_COOKIE_NAME, PREFERRED_LANGUAGE_COOKIE_NAME, } from '@yme/molecules/language-selector/constants';
import { getPreparedCountryItems, getPreparedLanguageItems, } from '@yme/molecules/language-selector/helpers';
import { getLanguageSelectorProperties } from '@yme/molecules/language-selector/redux/selectors';
import { getIsInAuthor, getIsoCountryCode, getLocationMismatchModalModel, getRTL, } from '@yme/selectors/common';
import { useTranslation } from '@yme/services/i18n';
import { Network } from '@yme/services/network';
import { colorCoreBlack300 } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { reloadPage } from '@yme/utils/common';
import { setLocationMismatchBarOpen } from '../redux/actions';
import { getLocationMismatchBarOpen } from '../redux/selectors';
import { LOCATION_MISMATCH_BAR, LOCATION_MISMATCH_COUNTRY, LOCATION_MISMATCH_LANGUAGE, LOCATION_MISMATCH_CHECKED, } from './constants';
import { WrapperStyled, DropdownWrapperStyled, DescriptionStyled, ConfirmButtonStyled, } from './styled';
export function LocationMismatchBar() {
    const { countries, initialCountry, initialLanguage } = useSelector(getLocationMismatchModalModel);
    const [activeCountry, setActiveCountry] = useState(initialCountry);
    const [activeLanguage, setActiveLanguage] = useState(initialLanguage);
    const [userCountryCode, setUserCountryCode] = useState(cachedClientLocationCountry());
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const { currentLanguage } = useSelector(getLanguageSelectorProperties);
    const isoCountryCode = useSelector(getIsoCountryCode);
    const isRightToLeft = useSelector(getRTL);
    const isLocationMismatchBarOpen = useSelector(getLocationMismatchBarOpen);
    const isInAuthor = useSelector(getIsInAuthor);
    const { isMobileView, isInfiniteDesktopView } = useWindowSize();
    const languages = changeLanguageList(countries, activeCountry);
    const preparedLanguages = getPreparedLanguageItems(languages);
    const homePagePath = languages.find((language) => language.code === activeLanguage)?.homePagePath ?? '';
    const skipLocationMismatchCheck = !userCountryCode ||
        !isoCountryCode ||
        !countries.length ||
        isLocationMismatchBarOpen ||
        isInAuthor;
    useEffect(() => {
        if (skipLocationMismatchCheck) {
            return;
        }
        const uCode = userCountryCode?.toLowerCase();
        const userCountry = countries.find((country) => country.isoCode?.toLowerCase() === uCode);
        const locationMismatchChecked = Cookies.get(LOCATION_MISMATCH_CHECKED);
        const isCountryDiffers = uCode !== isoCountryCode?.toLowerCase();
        if (isCountryDiffers && userCountry && !locationMismatchChecked) {
            dispatch(setLocationMismatchBarOpen(true));
            setActiveCountry(userCountry.code);
            dispatch(setLocationMismatchInitialCountry(userCountry.code));
            const currentLanguage = changeLanguageList(countries, userCountry.code)[0]?.code ?? '';
            setActiveLanguage(currentLanguage);
            dispatch(setLocationMismatchInitialLanguage(currentLanguage));
        }
    }, [skipLocationMismatchCheck, countries, userCountryCode, isoCountryCode, dispatch]);
    useEffect(() => {
        // Assume user country likely not gonna change after session start
        if (userCountryCode) {
            return;
        }
        getClientLocationCountry().then((country) => {
            if (country) {
                setUserCountryCode(country);
            }
        });
    }, [userCountryCode]);
    const setLocationMismatchChecked = useCallback(() => {
        Cookies.set(LOCATION_MISMATCH_CHECKED, 'true');
    }, []);
    const closeLocationMismatch = useCallback(() => {
        setLocationMismatchChecked();
        dispatch(setLocationMismatchBarOpen(false));
    }, [dispatch, setLocationMismatchChecked]);
    const handleClose = useCallback(() => {
        if (isMobileView && !Cookies.get(OPTANON_ALERT_BOX_CLOSED)) {
            return;
        }
        closeLocationMismatch();
    }, [closeLocationMismatch, isMobileView]);
    const onSubmit = useCallback(() => {
        Cookies.set(PREFERRED_COUNTRY_COOKIE_NAME, activeCountry, {
            expires: COOKIE_EXPIRE_DAYS,
        });
        Cookies.set(PREFERRED_LANGUAGE_COOKIE_NAME, activeLanguage, {
            expires: COOKIE_EXPIRE_DAYS,
        });
        closeLocationMismatch();
        if (activeCountry !== currentLanguage.country || activeLanguage !== currentLanguage.language) {
            setIsLoading(true);
            Network.get(TARGET_PAGE_SERVICE_ENDPOINT, {
                params: {
                    currentPagePath: window.location.pathname.split('.html')[0],
                    targetHomePagePath: homePagePath,
                    targetLocaleAndLanguage: `/${activeCountry}/${activeLanguage}`,
                },
            })
                .then((navLink) => {
                const hash = history.location.hash;
                const pathname = navLink.data;
                history.push({
                    pathname,
                    hash: pathname.includes('/pdp') ? hash : '',
                });
            })
                .catch(() => {
                history.push({
                    pathname: homePagePath || '',
                });
            })
                .finally(() => {
                // because we unable to change ROOT model in runtime (likely break analytics and other stuff)
                // we need to reload page to apply new language
                reloadPage();
            });
        }
    }, [
        activeCountry,
        activeLanguage,
        closeLocationMismatch,
        currentLanguage.country,
        currentLanguage.language,
        history,
        homePagePath,
    ]);
    const onChangeActiveCountryItem = useCallback((value) => {
        setActiveCountry(value);
        setActiveLanguage(changeLanguageList(countries, value)[0]?.code ?? '');
    }, [countries]);
    const onChangeActiveLanguageItem = useCallback((value) => {
        setActiveLanguage(value);
    }, []);
    const closeButton = (React.createElement(Button, { onClick: handleClose, buttonVariant: TEXT_VARIANT, hasIconButton: ICON_ONLY, size: SIZE_36, iconComponent: CloseIcon }));
    if (isLoading) {
        return (React.createElement(SpinnerLoader, { isLoading: true, fullWidth: true }));
    }
    if (!isLocationMismatchBarOpen) {
        return null;
    }
    return (React.createElement(WrapperStyled, { id: LOCATION_MISMATCH_BAR },
        React.createElement(GridWrapper, { containerProps: {
                isRightToLeft,
            } },
            React.createElement(FlexWrapper, { flexDirection: isInfiniteDesktopView ? 'row' : 'column', alignItems: isInfiniteDesktopView ? 'center' : undefined, justifyContent: isInfiniteDesktopView ? 'space-between' : undefined, gap: "16px" },
                React.createElement(FlexWrapper, { justifyContent: "space-between", alignItems: isMobileView ? 'start' : 'center' },
                    React.createElement(DescriptionStyled, { color: colorCoreBlack300 },
                        t('locationMismatch_popUp_description'),
                        " ",
                        t('languageSelector_country_hint'),
                        "."),
                    !isInfiniteDesktopView && closeButton),
                React.createElement(FlexWrapper, { flexDirection: isMobileView ? 'column' : 'row', alignItems: isInfiniteDesktopView ? 'center' : undefined, gap: "16px" },
                    React.createElement(DropdownWrapperStyled, null,
                        React.createElement(Dropdown, { id: LOCATION_MISMATCH_COUNTRY, hasAutocomplete: true, isInheritBackground: true, onChange: onChangeActiveCountryItem, items: getPreparedCountryItems(countries), activeItem: activeCountry, inputPlaceholder: t('languageSelector_dropdown_placeholder'), isRightToLeft: isRightToLeft, className: "items-with-icons" })),
                    React.createElement(DropdownWrapperStyled, null,
                        React.createElement(Dropdown, { id: LOCATION_MISMATCH_LANGUAGE, isInheritBackground: true, onChange: onChangeActiveLanguageItem, items: preparedLanguages, isDisabled: preparedLanguages.length <= 1, activeItem: activeLanguage, isRightToLeft: isRightToLeft })),
                    React.createElement(ConfirmButtonStyled, { onClick: onSubmit, label: t('locationMismatch_popUp_confirm_button'), buttonVariant: GHOST_VARIANT, hasIconButton: NO_ICON, size: SIZE_40 }),
                    isInfiniteDesktopView && closeButton)))));
}
